import { CommonModule } from '@angular/common';
import { Component, OnDestroy, OnInit } from '@angular/core';
import { RouterModule } from '@angular/router';
import { NavigationItem } from '@core/models/interfaces/system';
import { Store } from '@ngrx/store';
import { TranslateModule } from '@ngx-translate/core';
import { NgxPermissionsModule, NgxPermissionsService } from 'ngx-permissions';
import { SidebarModule } from 'primeng/sidebar';

import { FormsModule } from '@angular/forms';
import { SIDEBAR } from '@core/constants/sidebar';
import { APP_PERMISSION } from '@core/enum/permisson';
import { FacebookAccount } from '@core/models/interfaces/facebook/facebook-account';
import { IntegrationItem } from '@core/models/interfaces/facebook/integration';
import { FacebookAdsAdminService } from '@core/services/facebook.service.ts/facebook-ads-admin.service';
import { Actions, ofType } from '@ngrx/effects';
import { AppState } from '@state/app.state';
import { refreshBusinessPortfolio, setBusinessIdSelected, setBusinessNameSelected } from '@state/auth/auth.actions';
import { selectBusinessIdSelected, selectFacebookAccountSelected } from '@state/auth/auth.selectors';
import { setIsSidebar } from '@state/system/system.actions';
import { selectIsSidebar } from '@state/system/system.selectors';
import { DropdownModule } from 'primeng/dropdown';
import { SkeletonModule } from 'primeng/skeleton';
import { MessageService } from 'primeng/api';
import { Subject, takeUntil } from 'rxjs';
import { environment } from 'src/environments/environment';

@Component({
  selector: 'app-sidebar',
  standalone: true,
  imports: [
    CommonModule,
    FormsModule,
    SidebarModule,
    RouterModule,
    TranslateModule,
    NgxPermissionsModule,
    DropdownModule,
    SkeletonModule
  ],
  templateUrl: './sidebar.component.html',
  styleUrl: './sidebar.component.scss'
})
export class SidebarComponent implements OnInit, OnDestroy {
  readonly navigationItems = SIDEBAR;
  readonly unsubscribe$ = new Subject();
  readonly env = environment;
  readonly APP_PERMISSION = APP_PERMISSION;

  businesses: IntegrationItem[] = [];
  isLoadingBusiness: boolean = false;
  businessIdSelected = '';
  isMenuShow = true;
  showChildren: boolean[] = [];
  facebookAccount: FacebookAccount;
  isSidebar$ = this.store.select(selectIsSidebar);

  constructor(
    private store: Store<AppState>,
    private facebookAdsAdminService: FacebookAdsAdminService,
    private ngxPermissionsService: NgxPermissionsService,
    private actions$: Actions,
    private messageService: MessageService
  ) {}

  ngOnInit() {
    this.showChildren = this.navigationItems.map(item => !!item.children);
    this.subscribeFacebookAccountSelected();
    this.subscribeBusinessIdSelected();
    this.subscribeRefreshBusiness();
  }

  subscribeRefreshBusiness() {
    this.actions$
      .pipe(ofType(refreshBusinessPortfolio))
      .pipe(takeUntil(this.unsubscribe$))
      .subscribe(() => {
        this.fetchBusiness();
        this.refreshUserAccessToken();
      });
  }

  subscribeFacebookAccountSelected() {
    this.store
      .select(selectFacebookAccountSelected)
      .pipe(takeUntil(this.unsubscribe$))
      .subscribe(account => {
        if (account) {
          this.facebookAccount = account;
          this.fetchBusiness();
          this.refreshUserAccessToken();
          this.ngxPermissionsService.addPermission(APP_PERMISSION.FACEBOOK_LOGGED_IN);
        }
      });
  }

  subscribeBusinessIdSelected() {
    this.store
      .select(selectBusinessIdSelected)
      .pipe(takeUntil(this.unsubscribe$))
      .subscribe(businessIdSelected => {
        const idInit = sessionStorage.getItem('businessIdInit') || undefined;
        this.businessIdSelected = idInit || businessIdSelected;
      });
  }

  fetchBusiness() {
    if (!this.facebookAccount) return;
    this.isLoadingBusiness = true;
    this.facebookAdsAdminService
      .getBusinessPortfolio(this.facebookAccount.accessToken, this.facebookAccount.id)
      .pipe(takeUntil(this.unsubscribe$))
      .subscribe({
        next: res => {
          const data = res.data;
          if (data?.length) {
            this.businesses = data;
            const businessId = sessionStorage.getItem('businessIdInit');
            const selectedBusiness = this.businesses.find(item => item.id === businessId) || data[0];

            this.store.dispatch(setBusinessIdSelected({ id: selectedBusiness.id }));
            this.store.dispatch(setBusinessNameSelected({ name: selectedBusiness.name }));
          } else {
            this.businesses = [];
            this.store.dispatch(setBusinessIdSelected({ id: '' }));
            this.store.dispatch(setBusinessNameSelected({ name: '' }));
          }

          this.isLoadingBusiness = false;
        },
        error: () => {
          this.isLoadingBusiness = false;
        }
      });
  }

  refreshUserAccessToken() {
    this.facebookAdsAdminService.refreshUserAccessToken(this.facebookAccount.accessToken).subscribe({
      next: () => {},
      error: err => {
        if (err.status === 401) {
          this.messageService.add({ severity: 'error', detail: 'Session expired, please reconnect' });
        } else {
          this.messageService.add({ severity: 'error', detail: 'Failed to refresh access token. Please reconnect' });
        }
      }
    });
  }

  onChangeBusiness() {
    sessionStorage.setItem('businessIdInit', this.businessIdSelected);
    const selectedBusiness = this.businesses.find(item => item.id === this.businessIdSelected);
    this.store.dispatch(setBusinessIdSelected({ id: this.businessIdSelected }));
    this.store.dispatch(setBusinessNameSelected({ name: selectedBusiness?.name || '' }));
  }

  setIsSidebar(isSidebar: boolean) {
    this.store.dispatch(setIsSidebar({ isSidebar }));
  }

  onClickItem(item: NavigationItem, index: number) {
    if (item.children) {
      this.showChildren[index] = !this.showChildren[index];
    } else {
      this.setIsSidebar(false);
    }
  }

  ngOnDestroy() {
    this.unsubscribe$.next(null);
    this.unsubscribe$.complete();
  }
}
